/**
 * @file ShorePayConnectScreen.tsx
 *
 * The routed Shore Pay screen for `/payments/shore-pay/connect`, which is
 * state-aware: it reads the tenant's payment methods on mount and renders either
 *  - the CONNECT form (merchant email + country) when no Shore account exists yet.
 *    On submit it forwards the details to the engine
 *    (`POST /v1/payment-methods/shore/connect`, via {@link connectShorePay}), which
 *    hands them to the payment-service's CE-only onboarding route and returns the
 *    hosted KYC `actionUrl` to redirect to; or
 *  - the {@link ShorePayManageCard} (enable/disable + KYC link) once connected.
 *
 * Activation is asynchronous: Shore's `account.updated` webhook flips the method to
 * enabled in the payment-service, and the overview badge reflects it on the next read.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ContentContainer, GeneralField, Icon, Loader } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { notifyError } from '../../feedback/toast';
import { connectShorePay, fetchPaymentGateways } from '../../api/payments';
import type { PaymentGateway } from '../../api/payments-types';
import { FormFooter } from '../../components/FormFooter';
import { loadContinents } from '../../shipping/region-data';
import { localizeContinents } from '../../shipping/region-localization';
import { getCountryOptions } from '../../shipping/shipping-region-utils';
import { FieldShell, SelectControl, type PaymentSelectOption } from '../PaymentFormField';
import { SHORE_GATEWAY_ID, SHORE_PAY_TERMS_URL, STRIPE_TERMS_URL } from '../payments-constants';
import { ShorePayManageCard } from './ShorePayManageCard';
import styles from '../payments.module.css';

/** Basic e-mail shape check (defence-in-depth; the payment-service validates too). */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Renders the routed Shore Pay connect/manage screen.
 *
 * @returns The connect form, the manage card, or a loader.
 */
export function ShorePayConnectScreen() {
  const intl = useIntl();
  const [loading, setLoading] = useState(true);
  const [shoreGateway, setShoreGateway] = useState<PaymentGateway>();
  const [countryOptions, setCountryOptions] = useState<PaymentSelectOption[]>([]);

  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [emailError, setEmailError] = useState<string>();
  const [countryError, setCountryError] = useState<string>();
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string>();

  // Read the tenant's methods once: a present Shore gateway means an account already
  // exists (show the manage card); its absence means a first connect (show the form).
  useEffect(() => {
    let active = true;
    void fetchPaymentGateways()
      .catch(() => [] as PaymentGateway[])
      .then((gateways) => {
        if (!active) return;
        setShoreGateway(gateways.find((g) => g.id === SHORE_GATEWAY_ID));
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Country `<select>` options, localized per the active admin locale.
  useEffect(() => {
    let active = true;
    void loadContinents()
      .then((continents) => {
        if (active) {
          setCountryOptions(getCountryOptions(localizeContinents(continents, intl.locale)));
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [intl.locale]);

  if (loading) {
    return (
      <div className="gv-flex gv-justify-center gv-p-lg">
        <Loader condensed />
      </div>
    );
  }

  if (shoreGateway) {
    return <ShorePayManageCard gateway={shoreGateway} />;
  }

  const validate = (): boolean => {
    let ok = true;
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError(intl.formatMessage(adminMessages.paymentsShoreEmailRequired));
      ok = false;
    } else if (!EMAIL_RE.test(trimmedEmail)) {
      setEmailError(intl.formatMessage(adminMessages.paymentsShoreEmailInvalid));
      ok = false;
    } else {
      setEmailError(undefined);
    }
    if (!country) {
      setCountryError(intl.formatMessage(adminMessages.paymentsShoreCountryRequired));
      ok = false;
    } else {
      setCountryError(undefined);
    }
    return ok;
  };

  const handleConnect = async (): Promise<void> => {
    if (!validate()) return;
    setConnecting(true);
    setConnectError(undefined);
    try {
      const { actionUrl } = await connectShorePay({ email: email.trim(), country });
      // Hand the tab to the Shore/Stripe hosted KYC; it redirects back to the
      // Payments overview when done (payment-service `connectRedirectUri`).
      window.location.assign(actionUrl);
    } catch {
      setConnectError(intl.formatMessage(adminMessages.paymentsShoreConnectError));
      notifyError(intl.formatMessage(adminMessages.toastError));
      setConnecting(false);
    }
  };

  return (
    <div className={styles.form}>
      <header className="gv-flex gv-flex-col gv-w-full gv-gap-sm">
        <div className="gv-breadcrumbs">
          <Link to="/payments">
            <Icon name="arrow_back" size="small" />
            <FormattedMessage {...adminMessages.formBack} />
          </Link>
        </div>
        <h2 className="gv-heading-lg gv-m-0">
          <FormattedMessage {...adminMessages.paymentsShoreConnectTitle} />
        </h2>
        <div className="gv-text-sm">
          <FormattedMessage {...adminMessages.paymentsShoreConnectDescription} />
        </div>
      </header>

      <div className={`gv-flex gv-flex-col gv-gap-lg gv-w-full ${styles.column}`}>
        <ContentContainer className="gv-p-lg gv-flex gv-flex-col gv-gap-md">
          <FieldShell
            id="shore-pay-email"
            label={intl.formatMessage(adminMessages.paymentsShoreEmail)}
            error={emailError}
          >
            <GeneralField
              id="shore-pay-email"
              value={email}
              error={emailError}
              data-testid="shore-pay-connect-email"
              aria-label={intl.formatMessage(adminMessages.paymentsShoreEmail)}
              onChange={(event) => setEmail(event.target.value)}
            />
          </FieldShell>

          <FieldShell
            id="shore-pay-country"
            label={intl.formatMessage(adminMessages.paymentsShoreCountry)}
            error={countryError}
          >
            <SelectControl
              id="shore-pay-country"
              value={country}
              testId="shore-pay-connect-country"
              ariaLabel={intl.formatMessage(adminMessages.paymentsShoreCountry)}
              options={countryOptions}
              placeholder={intl.formatMessage(adminMessages.paymentsShoreCountryPlaceholder)}
              onChange={(event) => setCountry(event.target.value)}
            />
          </FieldShell>

          <div className="gv-text-sm gv-text-bold">
            <FormattedMessage {...adminMessages.paymentsShoreTermsPrefix} />{' '}
            <a href={SHORE_PAY_TERMS_URL} target="_blank" rel="noreferrer">
              <FormattedMessage {...adminMessages.paymentsShoreTermsShorePay} />
            </a>{' '}
            <FormattedMessage {...adminMessages.paymentsShoreTermsJoiner} />{' '}
            <a href={STRIPE_TERMS_URL} target="_blank" rel="noreferrer">
              <FormattedMessage {...adminMessages.paymentsShoreTermsStripe} />
            </a>
            .
          </div>
        </ContentContainer>

        {connectError ? (
          <p
            className="gv-text-sm gv-state-critical"
            role="alert"
            data-testid="shore-pay-connect-error"
          >
            {connectError}
          </p>
        ) : null}

        <FormFooter>
          <Link
            to="/payments"
            className="gv-button gv-button-cancel"
            data-testid="shore-pay-connect-back"
          >
            <FormattedMessage {...adminMessages.formBack} />
          </Link>
          <button
            type="button"
            className="gv-button gv-button-primary"
            data-testid="shore-pay-connect-submit"
            onClick={() => void handleConnect()}
            disabled={connecting}
          >
            {connecting ? (
              <FormattedMessage {...adminMessages.paymentsShoreConnecting} />
            ) : (
              <FormattedMessage {...adminMessages.paymentsActionConnect} />
            )}
          </button>
        </FormFooter>
      </div>
    </div>
  );
}
